export const PT_TO_MM = 0.3528;
export const BASE_PX_PER_MM = 6;

export const CARD_SIZES = [
  { id: "cr80", label: "CR80 PVC / ID — 85.6 × 54 mm", w: 85.6, h: 54 },
  { id: "cr80p", label: "CR80 Portrait — 54 × 85.6 mm", w: 54, h: 85.6 },
  { id: "biz", label: "Business Card — 89 × 51 mm", w: 89, h: 51 },
  { id: "biz-eu", label: "Business EU — 85 × 55 mm", w: 85, h: 55 },
  { id: "cr79", label: "CR79 Adhesive — 84 × 52 mm", w: 84, h: 52 },
  { id: "tag", label: "Jewellery Tag — 60 × 40 mm", w: 60, h: 40 },
  { id: "cert-l", label: "Large Certificate — 100 × 70 mm", w: 100, h: 70 },
];

export const FONTS = [
  "Plus Jakarta Sans", "JetBrains Mono", "Playfair Display", "Cinzel", "Montserrat",
  "Poppins", "Roboto Slab", "Great Vibes", "Oswald", "Lora", "Arial", "Georgia",
];

export const CATEGORY_LABEL = {
  id_card: "ID Card", business_card: "Business", membership: "Membership",
  jewellery: "Jewellery Cert", custom: "Custom",
};

const PLACEHOLDER = /\{\{\s*([\w.-]+)\s*\}\}/g;

export const fillTemplate = (str, data) =>
  (str || "").replace(PLACEHOLDER, (m, k) => (data ? data[k] ?? "" : k.startsWith("_STORE_") ? storeVars()[k] || "" : m));

export function extractFields(tpl) {
  const seen = new Map();
  ["front", "back"].forEach((s) => {
    (tpl?.[s]?.elements || []).forEach((el) => {
      const src = el.type === "text" ? el.text : ["qr", "barcode"].includes(el.type) ? el.value : "";
      for (const m of (src || "").matchAll(PLACEHOLDER)) if (!seen.has(m[1]) && !m[1].startsWith("_")) seen.set(m[1], "text");
      if (el.type === "image" && el.field) seen.set(el.field, "image");
    });
  });
  return [...seen.entries()].map(([key, type]) => ({ key, type }));
}

export const uid = () => Math.random().toString(36).slice(2, 12);
const r2 = (n) => Math.round(n * 100) / 100;

const centered = (tpl, w, h) => ({ x: r2(tpl.width / 2 - w / 2), y: r2(tpl.height / 2 - h / 2), w, h });

const textEl = (tpl, w, h, extra) => ({
  id: uid(), type: "text", rotation: 0, opacity: 1, ...centered(tpl, w, h),
  text: "Your text", fontSize: 7, fontWeight: 500, color: "#0F172A", align: "left",
  fontFamily: "Plus Jakarta Sans", italic: false, letterSpacing: 0, uppercase: false, lineHeight: 1.15,
  ...extra,
});

const shapeEl = (tpl, type, w, h, extra) => ({
  id: uid(), type, rotation: 0, opacity: 1, ...centered(tpl, w, h),
  fill: "#6366F1", stroke: "transparent", strokeWidth: 0, radius: 0, ...extra,
});

const imageEl = (tpl, w, h, extra) => ({
  id: uid(), type: "image", rotation: 0, opacity: 1, ...centered(tpl, w, h),
  src: "", field: "", fit: "contain", radius: 0, stroke: "transparent", strokeWidth: 0, ...extra,
});

const qrEl = (tpl, s, extra) => ({
  id: uid(), type: "qr", rotation: 0, opacity: 1, ...centered(tpl, s, s),
  value: "https://example.com", color: "#000000", bg: "#FFFFFF", ...extra,
});

let SITE = null;
export const setSiteCache = (s) => { SITE = s || null; };
export const getSiteCache = () => SITE;
export const verifyUrl = (rid) => {
  if (SITE?.enabled && SITE.qr_target !== "plain" && SITE.slug)
    return SITE.domain ? `https://${SITE.domain}/certificate/${rid}` : `${window.location.origin}/store/${SITE.slug}/certificate/${rid}`;
  return `${window.location.origin}/verify/${rid}`;
};
let BRAND = {};
export const setBrandCache = (b) => { BRAND = b || {}; };
export const storeVars = () => ({
  _STORE_NAME: BRAND.store_name || "", _STORE_CITY: BRAND.city || "", _STORE_PHONE: BRAND.phone || "",
  _STORE_ADDRESS: BRAND.address || "", _STORE_EMAIL: BRAND.email || "", _STORE_WEBSITE: BRAND.website || "",
});
export const withSystem = (record) => ({ ...storeVars(), ...record.data, _VERIFY_URL: verifyUrl(record.id), _RECORD_ID: record.id });

export function purityToKarat(purity) {
  const m = String(purity ?? "").match(/\d+(?:\.\d+)?/);
  const n = m ? parseFloat(m[0]) : NaN;
  return n > 0 && n <= 100 ? ((n * 24) / 100).toFixed(2) : "";
}

const barcodeEl = (tpl, extra) => ({
  id: uid(), type: "barcode", rotation: 0, opacity: 1, ...centered(tpl, 40, 10),
  value: "{{EMP_ID}}", format: "CODE128", color: "#000000", bg: "transparent", showText: true, fontSize: 5, ...extra,
});

export function makeElement(kind, tpl, extra = {}) {
  switch (kind) {
    case "heading": return textEl(tpl, 50, 7, { text: "Heading", fontSize: 12, fontWeight: 800 });
    case "text": return textEl(tpl, 40, 5, { text: "Your text here" });
    case "field": return textEl(tpl, 40, 5, { text: `{{${extra.key}}}`, fontWeight: 600 });
    case "rect": return shapeEl(tpl, "rect", 30, 15, { radius: 1 });
    case "ellipse": return shapeEl(tpl, "ellipse", 15, 15);
    case "line": return shapeEl(tpl, "rect", 40, 0.4, { fill: "#0F172A" });
    case "image": return imageEl(tpl, 20, 20, extra);
    case "photo": return imageEl(tpl, 22, 28, { field: "PHOTO", fit: "cover", radius: 1 });
    case "qr": return qrEl(tpl, 15, extra);
    case "barcode": return barcodeEl(tpl, extra);
    default: return null;
  }
}

const GOLD = "#B8860B";

export function makeJewellery(kind, tpl, opts = {}) {
  const W = tpl.width, H = tpl.height;
  switch (kind) {
    case "title":
      return [textEl(tpl, W - 10, 5, { x: 5, y: 4, text: "CERTIFICATE OF AUTHENTICITY", fontFamily: "Cinzel", fontSize: 7, fontWeight: 800, color: "#78350F", align: "center", letterSpacing: 1 })];
    case "border":
      return [
        shapeEl(tpl, "rect", W - 3, H - 3, { x: 1.5, y: 1.5, fill: "transparent", stroke: GOLD, strokeWidth: 0.6, radius: 1.5 }),
        shapeEl(tpl, "rect", W - 5, H - 5, { x: 2.5, y: 2.5, fill: "transparent", stroke: "#D4A017", strokeWidth: 0.2, radius: 1 }),
      ];
    case "hallmark": {
      const c = shapeEl(tpl, "ellipse", 14, 14, { fill: "transparent", stroke: GOLD, strokeWidth: 0.6 });
      return [c, textEl(tpl, 14, 5, { x: c.x, y: c.y + 4.5, text: opts.text || "916", fontSize: 9, fontWeight: 800, color: GOLD, align: "center", fontFamily: "Cinzel" })];
    }
    case "badge": {
      const b = shapeEl(tpl, "rect", 26, 6, { fill: "linear-gradient(90deg,#B8860B,#FDE68A,#B8860B)", radius: 3 });
      return [b, textEl(tpl, 26, 4, { x: b.x, y: b.y + 1.3, text: "100% GENUINE", fontSize: 5.5, fontWeight: 800, color: "#422006", align: "center", letterSpacing: 1 })];
    }
    case "spec": {
      const y = r2(opts.y ?? H / 2);
      return [
        textEl(tpl, 20, 3, { x: 30, y, text: opts.label.toUpperCase(), fontSize: 4, fontWeight: 600, color: "#92400E", letterSpacing: 0.6 }),
        textEl(tpl, 30, 3, { x: 50, y, text: `{{${opts.key}}}`, fontSize: 5, fontWeight: 700, color: "#1C1917" }),
      ];
    }
    case "photo": return [imageEl(tpl, 22, 22, { field: "ITEM_PHOTO", fit: "cover", radius: 1, stroke: GOLD, strokeWidth: 0.4 })];
    case "qr": return [qrEl(tpl, 12, { value: "{{_VERIFY_URL}}", color: "#1C1917", bg: "#FFFBEB" })];
    case "signature": {
      const l = shapeEl(tpl, "rect", 24, 0.25, { fill: "#78350F" });
      return [l, textEl(tpl, 24, 3, { x: l.x, y: l.y + 0.8, text: "Authorised Signatory", fontSize: 3.8, color: "#78350F", align: "center" })];
    }
    default: return [];
  }
}

export const JEWELLERY_SPECS = [
  { label: "Cert No", key: "CERT_NO" },
  { label: "Item", key: "ITEM" },
  { label: "Metal / Purity", key: "METAL" },
  { label: "Gross Wt.", key: "GROSS_WT" },
  { label: "Net Wt.", key: "NET_WT" },
  { label: "Diamond Wt.", key: "DIAMOND_WT" },
  { label: "Color / Clarity", key: "CLARITY" },
  { label: "Cut", key: "CUT" },
  { label: "Stone", key: "STONE" },
  { label: "HUID", key: "HUID" },
  { label: "Price", key: "PRICE" },
];

export const COMMON_FIELDS = ["NAME", "DESIGNATION", "EMP_ID", "DEPARTMENT", "PHONE", "EMAIL", "BLOOD_GROUP", "VALID_TILL", "ADDRESS", "MEMBER_NO"];
